import type { ResponseError } from 'shared/types/response-errors';
import { ClientErrorCode } from './client-error-code';

/** Одна GraphQL-ошибка — минимум, нужный разбору: у части DTO в проекте нет `locations` / `path`. */
export interface GraphqlErrorEntry {
  message: string;
  extensions?: Partial<ResponseError['extensions']>;
}

/**
 * Ошибка запроса в одной форме — что бы ни прилетело в `catch`.
 *
 * Простой объект, а не наследник `Error`: он уходит в `rejectWithValue` и дальше в Redux, а там
 * допустимо только сериализуемое (`.claude/rules/architecture.md`). Пустые поля не заводятся.
 */
export interface ApiError {
  /** `extensions.code` GraphQL-ошибки — см. `BackendErrorCode`. */
  code?: number;
  /** Ошибка самого фронта (нет сети и т. п.). С `code` не смешивается. */
  clientCode?: ClientErrorCode;
  /** HTTP-статус. У GraphQL-отказа его ставит `graphqlBaseQuery` (`400`), а не бэкенд. */
  status?: number;
  /** Исходный текст — английский, для `console.error`, а не для пользователя. */
  message: string;
}

/**
 * Отказ GraphQL, брошенный репозиторием через `unwrapGraphql`.
 *
 * `code` — **строкой**: `miniSerializeError` RTK переносит в `SerializedError` только строковые
 * `name` / `message` / `stack` / `code`, и код доживает до экрана у thunk-а, который просто
 * `throw`. Обратно в число его приводит `toApiError`. `extensions` и `data` — для прежних
 * разборщиков (`manager/domain/template-name-conflict.ts`), читающих их напрямую.
 */
export class GraphqlError extends Error {
  readonly code?: string;
  readonly extensions?: GraphqlErrorEntry['extensions'];
  readonly data: GraphqlErrorEntry[];

  /**
   * `message` по умолчанию — `extensions.debugMessage`, если он есть: graphql-php кладёт его только
   * во внутренние (не client-safe) ошибки, где `message` — безликое «Internal server Error», а
   * причина — в нём. Отказы с кодом (`UserError`) его не несут. Текст — для консоли и логов.
   */
  constructor(errors: GraphqlErrorEntry[], message = errors[0]?.extensions?.debugMessage || errors[0]?.message || 'GraphQL error') {
    super(message);
    this.name = 'GraphqlError';
    this.data = errors;
    this.extensions = errors[0]?.extensions;

    const code = errors[0]?.extensions?.code;
    if (code !== undefined && code !== null && code !== '') this.code = String(code);
  }
}

/**
 * Текст, который axios ставит ошибке без ответа сервера. В axios 0.25 (наш) у неё нет `code`
 * — `ERR_NETWORK` появился в 1.x и тоже понимается.
 */
const AXIOS_NETWORK_MESSAGE = 'Network Error';

type Loose = Record<string, unknown>;

function isObject(value: unknown): value is Loose {
  return typeof value === 'object' && value !== null;
}

function readString(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined;
}

/** Код бэкенда — целое положительное число; строкой приходит из `SerializedError`. */
function readCode(value: unknown): number | undefined {
  if (typeof value === 'number') return Number.isInteger(value) && value > 0 ? value : undefined;
  if (typeof value === 'string' && /^\d+$/.test(value)) return Number(value);
  return undefined;
}

/**
 * Тело ответа строкой — текст ошибки, если это не разметка: на 502/504 nginx или прокси отдают
 * HTML-страницу, и она не должна уехать целиком в `message`, журнал оверлея и `state.error`.
 */
function readBodyText(value: unknown): string | undefined {
  const text = readString(value);
  return text !== undefined && !/^\s*</.test(text) ? text : undefined;
}

function readStatus(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function isClientErrorCode(value: unknown): value is ClientErrorCode {
  return Object.values(ClientErrorCode).includes(value as ClientErrorCode);
}

/** Первая GraphQL-ошибка: RTK Query кладёт массив `errors` в `data`, сырой ответ — в `data.errors`. */
function firstGraphqlError(data: unknown): Partial<GraphqlErrorEntry> | undefined {
  const errors = Array.isArray(data) ? data : isObject(data) ? data.errors : undefined;
  if (!Array.isArray(errors) || !isObject(errors[0])) return undefined;
  return errors[0] as Partial<GraphqlErrorEntry>;
}

/**
 * Ошибка Supabase (`{ message, code, details, hint }`): её `code` — код Postgres вроде `23505`,
 * он похож на код бэкенда, но им не является.
 */
function isSupabaseError(error: Loose): boolean {
  return 'hint' in error || 'details' in error;
}

function isNetworkError(error: Loose, response: Loose | undefined, status: number | undefined): boolean {
  if (error.code === 'ERR_NETWORK' || error.status === 'FETCH_ERROR') return true;
  // axios 0.25 и base query-и RTK (они не переносят `response`) узнаются по тексту без статуса.
  return error.message === AXIOS_NETWORK_MESSAGE && status === undefined && response === undefined;
}

function compact(error: ApiError): ApiError {
  const result: ApiError = { message: error.message };
  if (error.code !== undefined) result.code = error.code;
  if (error.clientCode !== undefined) result.clientCode = error.clientCode;
  if (error.status !== undefined) result.status = error.status;
  return result;
}

/**
 * Любая пойманная ошибка → `ApiError`. Понимает все формы, которые встречаются в проекте:
 * - RTK Query GraphQL — `{ status: 400, data: errors[], message }` (`graphqlBaseQuery`);
 * - RTK Query REST — `{ status, data: { message } }` (`restBaseQuery`);
 * - ошибку axios — `{ response: { status, data } }`, без `response` — нет сети;
 * - `SerializedError` thunk-а — `{ name, message, code: '1404' }`;
 * - строку от `rejectWithValue(message)`;
 * - ошибку Supabase — `{ message, code, details, hint }`;
 * - `GraphqlError` из `unwrapGraphql` и уже готовый `ApiError`.
 */
export function toApiError(error: unknown): ApiError {
  if (typeof error === 'string') return { message: error };
  if (!isObject(error)) return { message: error === undefined || error === null ? '' : String(error) };

  const response = isObject(error.response) ? error.response : undefined;
  const status = readStatus(error.status) ?? readStatus(response?.status);
  const data = 'data' in error ? error.data : response?.data;
  const graphqlError = firstGraphqlError(data);

  // `debugMessage` graphql-php кладёт только во внутренние ошибки, где `message` — безликое
  // «Internal server Error»; причина — в нём (так же решает `GraphqlError`). Отказы с кодом его не несут.
  const message =
    readString(graphqlError?.extensions?.debugMessage) ??
    readString(graphqlError?.message) ??
    (isObject(data) ? readString(data.message) : readBodyText(data)) ??
    readString(error.message) ??
    readString(error.error) ??
    '';

  if (isClientErrorCode(error.clientCode)) return compact({ clientCode: error.clientCode, status, message });
  if (isNetworkError(error, response, status)) return compact({ clientCode: ClientErrorCode.NETWORK, message });

  const code =
    readCode(graphqlError?.extensions?.code) ??
    (isSupabaseError(error) ? undefined : readCode(error.code)) ??
    (isObject(error.extensions) ? readCode(error.extensions.code) : undefined);

  return compact({ code, status, message });
}
